import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, PencilSquareIcon, SparklesIcon } from '@heroicons/react/24/outline';
import CitedMarkdown from '../../../../../components/markdown/CitedMarkdown';
import { getTranscriptionLanguageName } from '../../../../../constants/transcriptionLanguages';
import ReportLanguageMenu from './ReportLanguageMenu';
import ReportSection, { SECTION_ICONS } from './ReportSection';

const NOTE_TYPES = {
  confirmation: { box: 'bg-green-50 border-green-200', badge: 'bg-green-100', text: 'text-green-700', icon: '✓', label: 'Confirmed' },
  context: { box: 'bg-blue-50 border-blue-200', badge: 'bg-blue-100', text: 'text-blue-700', icon: 'ℹ', label: 'Additional Context' },
  correction: { box: 'bg-red-50 border-red-200', badge: 'bg-red-100', text: 'text-red-700', icon: '!', label: 'Correction' },
};

const IMPORTANCE = {
  high: 'bg-red-100 text-red-600',
  medium: 'bg-amber-100 text-amber-600',
};

const SourceTag = ({ num }) => (
  <span className="inline-flex items-center justify-center text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 shrink-0 min-w-[28px]">
    S{num}
  </span>
);

/** The official session report: summary, full report and the knowledge-base extras. */
export default function ReportTab({ sessionId, primaryLanguage, report, canGenerate, onGenerate, onCitationClick }) {
  const { markdown, enrichment, lang, translatingCode } = report;
  const { transcriptReferences, sourceReferences, relatedResources, factualNotes } = enrichment;
  const citations = { transcriptReferences, sourceReferences, onCitationClick };
  const sources = Object.entries(sourceReferences || {});
  const editLink = `/sessions/${sessionId}/report/edit${report.isTranslated ? `?lang=${lang}` : ''}`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="border-b px-4 py-2 flex gap-2 items-center justify-between">
        <div className="session-report-head">
          <SparklesIcon />
          <span>Session report</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <ReportLanguageMenu report={report} primaryLanguage={primaryLanguage} />
          <Link to={editLink} className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm whitespace-nowrap">
            <PencilSquareIcon className="h-4 w-4 mr-1" />
            Edit Report
          </Link>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {translatingCode && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-center gap-3">
            <ArrowPathIcon className="h-5 w-5 animate-spin text-indigo-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-indigo-800">
                Translating report to {getTranscriptionLanguageName(translatingCode)}...
              </p>
              <p className="text-xs text-indigo-600 mt-0.5">This may take 1-2 minutes depending on the report length. You can continue browsing.</p>
            </div>
            <button
              type="button"
              onClick={() => report.cancelTranslation(translatingCode)}
              className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        )}

        {!report.hasContent && !report.busy && (
          <div className="session-detail-empty">
            <strong>No report generated yet.</strong>
            <span>Generate a report to see the content here.</span>
            {canGenerate && (
              <button type="button" className="session-btn session-btn-secondary" onClick={onGenerate} disabled={report.isStarting}>
                {report.isStarting ? 'Generating...' : 'Generate report'}
              </button>
            )}
          </div>
        )}

        <ReportSection title="Session at a glance" subtitle="Summary, keypoints, and speakers overview" icon={SECTION_ICONS.document}>
          <CitedMarkdown text={markdown.glance} maxLength={800} {...citations} />
        </ReportSection>

        <ReportSection title="Full session report" subtitle="Comprehensive analysis and detailed insights" icon={SECTION_ICONS.document}>
          <CitedMarkdown text={markdown.full} maxLength={1000} {...citations} />
        </ReportSection>

        {relatedResources.length > 0 && (
          <ReportSection
            title="Related Resources"
            subtitle={`Knowledge base sources related to the discussion topics (${relatedResources.length})`}
            icon={SECTION_ICONS.link}
            tone="teal"
          >
            <div className="space-y-3">
              {relatedResources.map((resource, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <SourceTag num={resource.ref_num} />
                  <div className="min-w-0 flex-1">
                    <a href={resource.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-teal-700 hover:underline">
                      {resource.title || 'Untitled'}
                    </a>
                    {resource.snippet && <CitedMarkdown text={resource.snippet} maxLength={150} className="text-xs text-gray-500 mt-1" />}
                    {resource.label && (
                      <span className="inline-block mt-1 text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{resource.label}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ReportSection>
        )}

        {factualNotes.length > 0 && (
          <ReportSection
            title="Factual Notes"
            subtitle={`Claims verified against the Diplo knowledge base (${factualNotes.length})`}
            icon={SECTION_ICONS.shield}
            tone="amber"
          >
            <div className="space-y-3">
              {factualNotes.map((note, idx) => {
                const type = NOTE_TYPES[note.type] || NOTE_TYPES.context;
                return (
                  <div key={idx} className={`border rounded-lg p-4 flex items-start gap-3 ${type.box}`}>
                    <span className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${type.badge} ${type.text}`}>{type.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold uppercase tracking-wide ${type.text}`}>{type.label}</span>
                        {note.importance && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${IMPORTANCE[note.importance] || 'bg-gray-100 text-gray-500'}`}>
                            {note.importance}
                          </span>
                        )}
                      </div>
                      {note.claim && <p className="text-xs text-gray-500 italic mb-1.5">"{note.claim}"</p>}
                      <CitedMarkdown
                        text={note.note || ''}
                        maxLength={300}
                        className="text-sm text-gray-700 leading-relaxed"
                        sourceReferences={sourceReferences}
                        onCitationClick={onCitationClick}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ReportSection>
        )}

        {sources.length > 0 && (
          <ReportSection title={`External Sources (${sources.length})`} icon={SECTION_ICONS.book} defaultOpen={false}>
            <div className="space-y-2">
              {sources.map(([num, ref]) => (
                <div key={num} className="flex items-start gap-2 text-xs">
                  <SourceTag num={num} />
                  <div className="min-w-0">
                    {ref.url
                      ? <a href={ref.url} target="_blank" rel="noopener noreferrer" className="font-medium text-teal-700 hover:underline">{ref.title || ref.url}</a>
                      : <span className="font-medium text-gray-700">{ref.title || 'Untitled'}</span>}
                    {ref.snippet && (
                      <span className="text-gray-500 ml-1">&mdash; {ref.snippet.length > 120 ? `${ref.snippet.slice(0, 120)}...` : ref.snippet}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ReportSection>
        )}
      </div>
    </div>
  );
}
